/* Monsuri · Corporate Gifting interactive bootstrap.
 *
 * One file, one IIFE, registers behaviour for every interactive
 * surface on /pages/corporate-gifting:
 *   - Mobile sticky bottom CTA (rAF-throttled scroll listener +
 *     IntersectionObserver to hide near the inquiry form)
 *   - Floating specialist tab toggle (open / close, focus trap,
 *     click-outside, Escape-to-close)
 *   - Multi-step form progression (handled by per-section JS,
 *     this file only wires the global progress-bar utility)
 *   - Use-case tile click → smooth scroll to bundles section
 *   - "Send this configuration" calculator CTA → form prefill
 *
 * Vanilla ES6, no dependencies. Loads once via the page template.
 * Idempotent: safe to call init() multiple times (theme editor
 * lifecycle).
 */

(function () {
  'use strict';

  /* Module-level idempotency: each init function uses data-corp-wired
   * markers on its root element so calling init() again (theme editor
   * shopify:section:load lifecycle) does not double-bind listeners. */

  /* ------------------------------------------------------------ *
   * 1. Mobile sticky bottom CTA
   * ------------------------------------------------------------ */
  function initMobileStickyCta() {
    var bar = document.querySelector('[data-corp-mobile-cta]');
    if (!bar) return;
    if (bar.dataset.corpWired === 'true') return;
    bar.dataset.corpWired = 'true';

    var inquiry = document.getElementById('inquiry');
    var ticking = false;
    var inquiryInView = false;

    if ('IntersectionObserver' in window && inquiry) {
      var io = new IntersectionObserver(function (entries) {
        inquiryInView = entries[0].isIntersecting;
        update();
      }, { threshold: 0.1 });
      io.observe(inquiry);
    }

    function update() {
      var threshold = window.innerHeight * 0.30;
      var passed = window.scrollY > threshold;
      var show = passed && !inquiryInView;
      if (show) {
        bar.removeAttribute('hidden');
        bar.setAttribute('data-corp-show', 'true');
      } else {
        bar.setAttribute('data-corp-show', 'false');
      }
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        update();
        ticking = false;
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    update();
  }

  /* ------------------------------------------------------------ *
   * 2. Use-case tile → smooth scroll to bundles
   * ------------------------------------------------------------ */
  function initUseCaseScroll() {
    var tiles = document.querySelectorAll('[data-corp-uc-tile]:not([data-corp-wired])');
    if (!tiles.length) return;
    tiles.forEach(function (tile) {
      tile.dataset.corpWired = 'true';
      tile.addEventListener('click', function (e) {
        var href = tile.getAttribute('href');
        if (!href || href.charAt(0) !== '#') return;
        var target = document.querySelector(href);
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        target.setAttribute('tabindex', '-1');
        try { target.focus({ preventScroll: true }); } catch (err) {}
      });
    });
  }

  /* ------------------------------------------------------------ *
   * 4. Multi-step inquiry form
   * ------------------------------------------------------------ */
  function initMultiStepForm() {
    var root = document.querySelector('[data-corp-form]');
    if (!root) return;
    if (root.dataset.corpWired === 'true') return;
    root.dataset.corpWired = 'true';
    var form = root.querySelector('form');
    var steps = root.querySelectorAll('[data-corp-form-step]');
    var progress = root.querySelector('[data-corp-form-progress]');
    var progressBars = root.querySelectorAll('[data-corp-progress-step]');
    var nextButtons = root.querySelectorAll('[data-corp-next]');
    var backButtons = root.querySelectorAll('[data-corp-back]');
    var fireKlaviyo = root.getAttribute('data-corp-klaviyo') === 'true';
    var klaviyoFired = false;

    if (!form || !steps.length) return;

    var currentStep = 1;

    function showStep(n, focusFirst) {
      // Default to moving focus (good for user-initiated Next/Back changes),
      // but the initial page-load render passes false: auto-focusing the
      // first field on load scrolled the page to the form near the bottom
      // (Safari ignores focus({preventScroll:true})), and auto-focus on load
      // is an a11y anti-pattern regardless.
      if (focusFirst === undefined) focusFirst = true;
      currentStep = n;
      steps.forEach(function (el) {
        var sn = parseInt(el.getAttribute('data-corp-form-step'), 10);
        if (sn === n) {
          el.removeAttribute('hidden');
          var firstField = el.querySelector('input, textarea, select');
          if (firstField && focusFirst) {
            try { firstField.focus({ preventScroll: true }); } catch (e) {}
          }
        } else {
          el.setAttribute('hidden', '');
        }
      });
      progressBars.forEach(function (el) {
        var sn = parseInt(el.getAttribute('data-corp-progress-step'), 10);
        el.classList.toggle('is-active', sn <= n);
        el.classList.toggle('is-current', sn === n);
        if (sn === n) el.setAttribute('aria-current', 'step');
        else el.removeAttribute('aria-current');
      });
      if (progress) {
        progress.setAttribute('aria-valuenow', String(n));
      }
    }

    function setFieldInvalid(field, invalid) {
      var wrapper = field.closest('.corp-form__field');
      if (invalid) {
        if (wrapper) wrapper.classList.add('corp-form__field--invalid');
        field.setAttribute('aria-invalid', 'true');
      } else {
        if (wrapper) wrapper.classList.remove('corp-form__field--invalid');
        field.removeAttribute('aria-invalid');
      }
    }

    function validateStep(n) {
      var step = root.querySelector('[data-corp-form-step="' + n + '"]');
      if (!step) return true;
      var requiredFields = step.querySelectorAll('[data-corp-required]');
      var ok = true;
      requiredFields.forEach(function (field) {
        var blank = !field.value || !String(field.value).trim();
        // Honor native constraints too (min, max, pattern, type) so a buyer
        // can't pass a number below the min on the multi-step transition
        // and only discover the error at submit, with the offending field
        // hidden in a prior step.
        var invalid = blank || (typeof field.checkValidity === 'function' && !field.checkValidity());
        setFieldInvalid(field, invalid);
        if (invalid) ok = false;
      });
      var emailField = step.querySelector('input[type="email"][data-corp-required]');
      if (emailField && emailField.value) {
        var rx = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
        if (!rx.test(String(emailField.value).trim())) {
          ok = false;
          setFieldInvalid(emailField, true);
        }
      }
      return ok;
    }

    function fireKlaviyoOnStep1() {
      if (klaviyoFired || !fireKlaviyo) return;
      if (typeof window._learnq === 'undefined' || !window._learnq) return;
      var emailField = form.querySelector('input[name="contact[email]"]');
      var nameField = form.querySelector('input[name="contact[Name]"]');
      var companyField = form.querySelector('input[name="contact[Company]"]');
      if (!emailField || !emailField.value) return;
      try {
        window._learnq.push(['identify', {
          $email: String(emailField.value).trim(),
          $first_name: nameField ? String(nameField.value || '').trim() : '',
          corporate_inquiry_company: companyField ? String(companyField.value || '').trim() : ''
        }]);
        window._learnq.push(['track', 'Corporate Inquiry — Step 1', {
          email: String(emailField.value).trim(),
          company: companyField ? String(companyField.value || '').trim() : '',
          source_url: window.location.href
        }]);
        klaviyoFired = true;
      } catch (e) {
        /* swallow — Klaviyo SDK not yet loaded; tracking is best-effort */
      }
    }

    nextButtons.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        if (!validateStep(currentStep)) return;
        if (currentStep === 1) fireKlaviyoOnStep1();
        if (currentStep < steps.length) showStep(currentStep + 1);
      });
    });

    backButtons.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        if (currentStep > 1) showStep(currentStep - 1);
      });
    });

    form.addEventListener('submit', function () {
      /* native Shopify {% form 'contact' %} handles posting + redirect.
         Last-step submit fires Klaviyo too in case step 1 was skipped
         on a fast typer. */
      fireKlaviyoOnStep1();
    });

    showStep(1, false);
  }

  /* ------------------------------------------------------------ *
   * 5. Pricing calculator
   * ------------------------------------------------------------ */
  function initCalculator() {
    var root = document.querySelector('[data-corp-calculator]');
    if (!root) return;
    if (root.dataset.corpWired === 'true') return;
    root.dataset.corpWired = 'true';

    var tierSelect = root.querySelector('[data-corp-tier]');
    var unitsInput = root.querySelector('[data-corp-units]');
    var unitsDisplay = root.querySelector('[data-corp-units-display]');
    var totalEl = root.querySelector('[data-corp-total]');
    var breakdownEl = root.querySelector('[data-corp-breakdown]');
    var discountMsg = root.querySelector('[data-corp-discount-msg]');
    var ctaEl = root.querySelector('[data-corp-calc-cta]');
    var addonInputs = root.querySelectorAll('[data-corp-addon]');
    if (!tierSelect || !unitsInput || !totalEl) return;

    var addRates = {
      note: parseFloat(root.dataset.corpAddNote) || 0,
      logo: parseFloat(root.dataset.corpAddLogo) || 0,
      multi: parseFloat(root.dataset.corpAddMulti) || 0
    };
    var disc25 = parseFloat(root.dataset.corpDiscount25) || 0;
    var disc50 = parseFloat(root.dataset.corpDiscount50) || 0;
    var disc100 = parseFloat(root.dataset.corpDiscount100) || 0;
    var ctaBaseHref = ctaEl ? ctaEl.getAttribute('href') : '#inquiry';

    function fmt(n) {
      return '$' + n.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }

    function discountPct(units) {
      if (units >= 100) return disc100;
      if (units >= 50) return disc50;
      if (units >= 25) return disc25;
      return 0;
    }

    function discountMessage(units) {
      var pct = discountPct(units);
      if (pct > 0) {
        return 'You qualify for a ' + pct + '% volume discount.';
      }
      var togo = 25 - units;
      if (togo <= 0) return '';
      return 'Add ' + togo + ' more set' + (togo === 1 ? '' : 's') + ' to unlock a ' + disc25 + '% volume discount.';
    }

    function selectedAddons() {
      var sel = {};
      addonInputs.forEach(function (input) {
        var key = input.getAttribute('data-corp-addon');
        sel[key] = input.checked;
      });
      return sel;
    }

    function makeRow(labelText, valueText, opts) {
      opts = opts || {};
      var li = document.createElement('li');
      if (opts.discount) li.classList.add('is-discount');
      var span = document.createElement('span');
      span.textContent = labelText;
      var b = document.createElement('b');
      b.textContent = valueText;
      li.appendChild(span);
      li.appendChild(b);
      return li;
    }

    function compute() {
      var units = parseInt(unitsInput.value, 10) || 0;
      var opt = tierSelect.options[tierSelect.selectedIndex];
      var price = opt ? (parseFloat(opt.getAttribute('data-corp-price')) || 0) : 0;
      var name = opt ? (opt.getAttribute('data-corp-name') || '') : '';
      var subtotal = price * units;
      var pct = discountPct(units);
      var discount = subtotal * (pct / 100);
      var addons = selectedAddons();
      var noteAdd = addons.note ? addRates.note * units : 0;
      var logoAdd = addons.logo ? addRates.logo * units : 0;
      var multiAdd = addons.multi ? addRates.multi * units : 0;
      var total = subtotal - discount + noteAdd + logoAdd + multiAdd;

      if (unitsDisplay) unitsDisplay.textContent = String(units);
      totalEl.textContent = fmt(total);

      if (breakdownEl) {
        while (breakdownEl.firstChild) breakdownEl.removeChild(breakdownEl.firstChild);
        breakdownEl.appendChild(makeRow(units + ' sets × $' + price, fmt(subtotal)));
        if (pct > 0) {
          breakdownEl.appendChild(makeRow('Volume discount (' + pct + '%)', '−' + fmt(discount), { discount: true }));
        }
        if (noteAdd > 0) breakdownEl.appendChild(makeRow('Branded note card', '+' + fmt(noteAdd)));
        if (logoAdd > 0) breakdownEl.appendChild(makeRow('Logo on packaging', '+' + fmt(logoAdd)));
        if (multiAdd > 0) breakdownEl.appendChild(makeRow('Multi-address shipping', '+' + fmt(multiAdd)));
      }
      if (discountMsg) discountMsg.textContent = discountMessage(units);

      if (ctaEl && ctaBaseHref) {
        var anchor = ctaBaseHref.indexOf('#') >= 0 ? ctaBaseHref.substring(ctaBaseHref.indexOf('#')) : '#inquiry';
        var addonList = [];
        if (addons.note) addonList.push('note');
        if (addons.logo) addonList.push('logo');
        if (addons.multi) addonList.push('multi-address');
        var prefill = '?corp_tier=' + encodeURIComponent(name) +
                      '&corp_units=' + units +
                      '&corp_addons=' + encodeURIComponent(addonList.join(','));
        ctaEl.setAttribute('href', prefill + anchor);
      }
    }

    tierSelect.addEventListener('change', compute);
    unitsInput.addEventListener('input', compute);
    addonInputs.forEach(function (input) {
      input.addEventListener('change', compute);
    });
    compute();
  }

  /* ------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------ */
  function init() {
    initMobileStickyCta();
    initUseCaseScroll();
    initMultiStepForm();
    initCalculator();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor section reload — Shopify dispatches a
     shopify:section:load on inserted sections. We don't know which
     sections those are, so re-run init() for any reload. */
  document.addEventListener('shopify:section:load', init);
})();
